import { routeHref } from '../../lib/router.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- mail-compose-dock ---
let __fez_component_0;
{
const MAX = 3
__fez_component_0 = class {
  GLOBAL = 'ComposeDock'

  init() {
    this.state.windows = []
    this.state.seq = 0
    this.state.senders = {}
  }

  async open({ container, mailbox, draft, to, subject, body } = {}) {
    if (draft) {
      const open = this.state.windows.find((win) => (this.compose(win.id)?.state.draft || win.draft) === draft)
      if (open) return this.patch(open.id, { minimized: false })
    }
    if (this.state.windows.length >= MAX) {
      const oldest = this.state.windows.find((win) => !win.minimized)
      if (oldest) this.patch(oldest.id, { minimized: true })
      if (this.state.windows.length >= MAX) return window.Toast.info('Close a message before opening another.')
    }
    const id = ++this.state.seq
    this.state.windows = [...this.state.windows, { id, container, draft: draft || '', minimized: false, ready: false, title: draft ? 'Draft' : 'New message' }]
    try {
      const [usable, saved] = await Promise.all([
        this.senders(container),
        draft ? window.App.api(`container/${container}/draft`, { draft }, { silent: true }) : null,
      ])
      const fields = saved
        ? {
            mailbox: saved.mailbox,
            domain: saved.domain || undefined,
            from_local: saved.from_local || undefined,
            to: saved.to,
            cc: saved.cc,
            bcc: saved.bcc,
            subject: saved.subject,
            body: saved.body,
            in_reply_to: saved.in_reply_to || undefined,
            attachments: saved.attachments,
          }
        : { mailbox, to, subject, body }
      this.patch(id, { ready: true, fields, mailboxes: usable.mailboxes, domains: usable.domains.map((name) => ({ name })), title: fields.subject || (draft ? 'Draft' : 'New message') })
    } catch (error) {
      window.Toast.error(error.message)
      this.remove(id)
    }
  }

  async senders(container) {
    if (!this.state.senders[container]) {
      const usable = await window.App.api(`container/${container}/my_mailboxes`, {}, { silent: true })
      this.state.senders = { ...this.state.senders, [container]: usable }
    }
    return this.state.senders[container]
  }

  patch(id, change) {
    this.state.windows = this.state.windows.map((win) => (win.id === id ? { ...win, ...change } : win))
  }

  remove(id) {
    this.state.windows = this.state.windows.filter((win) => win.id !== id)
  }

  compose(id) {
    return this.find(`[data-win="${id}"] .mail-compose-fez`)?.fez
  }

  toggle(id) {
    const win = this.state.windows.find((item) => item.id === id)
    if (win) this.patch(id, { minimized: !win.minimized })
  }

  // Leaving keeps what was written: a message with content is saved as a draft first.
  async keep(id) {
    const compose = this.compose(id)
    if (!compose || !compose.hasContent()) return compose?.state.draft || ''
    await compose.saveDraft()
    return compose.state.draft
  }

  async close(id) {
    await this.keep(id)
    this.remove(id)
  }

  async maximize(id) {
    const win = this.state.windows.find((item) => item.id === id)
    if (!win) return
    const draft = await this.keep(id)
    const mailbox = this.compose(id)?.state.fromMailbox || win.fields?.mailbox
    this.remove(id)
    window.App.go(routeHref('compose', { container: win.container, mailbox, draft }))
  }

  done(id) {
    this.remove(id)
  }

  titled(id, event) {
    if (event.target.name !== 'subject') return
    this.patch(id, { title: event.target.value.trim() || 'New message' })
  }

  CSS = `:fez {
display:contents;.dock{position:fixed;right:var(--space-6);bottom:0;z-index:var(--z-dropdown);display:flex;flex-direction:row-reverse;align-items:flex-end;gap:var(--space-3);pointer-events:none}.dock-win{display:flex;flex-direction:column;width:min(560px,calc(100vw - var(--rail-width) - 2 * var(--space-6)));max-height:calc(100vh - var(--space-12));border:1px solid var(--color-border-strong);border-bottom:0;border-radius:var(--radius-lg) var(--radius-lg) 0 0;background:var(--color-bg-raised);box-shadow:var(--shadow-lg);pointer-events:auto;&.min{width:280px}}.dock-head{display:flex;align-items:center;gap:var(--space-1);height:42px;padding:0 var(--space-2) 0 var(--space-4);border-radius:var(--radius-lg) var(--radius-lg) 0 0;background:var(--color-rail-bg);color:var(--color-sidebar-text-strong);cursor:pointer}.dock-title{flex:1;min-width:0;overflow:hidden;font-size:var(--text-sm);font-weight:var(--weight-semibold);text-overflow:ellipsis;white-space:nowrap}.dock-btn{display:grid;place-items:center;width:28px;height:28px;padding:0;border:0;border-radius:var(--radius-md);background:transparent;color:var(--color-sidebar-text);cursor:pointer;&:hover{background:var(--color-sidebar-hover-bg);color:var(--color-sidebar-text-strong)}}.dock-body{flex:1;min-height:0;overflow-y:auto;padding:var(--space-4);&.hidden{display:none}}.dock-loading{display:flex;justify-content:center;padding:var(--space-8)} :mobile {.dock{inset:0 0 calc(40px + 2 * var(--space-2)) 0;align-items:stretch}.dock-win{width:100%;max-height:none;border-radius:0;&.min{display:none}}.dock-win:not(:last-child){display:none}.dock-head{border-radius:0}.dock-body{padding-bottom:0}}
}`

  HTML = `<div class="dock" aria-live="polite">
{#each state.windows as win}
<section class="dock-win" class:min={win.minimized} key="win-{win.id}" data-win={win.id} fez:in="fly, y=32, duration=220" fez:out="fade, duration=150" aria-label={win.title}>
<header class="dock-head" onclick="fez.toggle({win.id})">
<span class="dock-title">{win.title}</span>
<button type="button" class="dock-btn" title={win.minimized ? 'Restore' : 'Minimize'} onclick="event.stopPropagation(); fez.toggle({win.id})">
{#if win.minimized}<ui-icon name="caret-up" size="16"></ui-icon>{:else}<ui-icon name="minus" size="16"></ui-icon>{/if}
</button>
<button type="button" class="dock-btn" title="Open full page" onclick="event.stopPropagation(); fez.maximize({win.id})"><ui-icon name="arrows-out-simple" size="16"></ui-icon></button>
<button type="button" class="dock-btn" title="Save and close" onclick="event.stopPropagation(); fez.close({win.id})"><ui-icon name="x" size="16"></ui-icon></button>
</header>
<div class="dock-body" class:hidden={win.minimized} oninput="fez.titled({win.id}, event)">
{#if win.ready}
<div fez:keep="compose-{win.id}">
<mail-compose container={win.container} :mailbox="win.fields.mailbox" :domain="win.fields.domain" :from_local="win.fields.from_local" :mailboxes="win.mailboxes" :domains="win.domains" :to="win.fields.to" :cc="win.fields.cc" :bcc="win.fields.bcc" :subject="win.fields.subject" :body="win.fields.body" :in_reply_to="win.fields.in_reply_to" :draft="win.draft" :attachments="win.fields.attachments" autofocus :on_done="() => fez.done(win.id)"></mail-compose>
</div>
{:else}
<div class="dock-loading"><ui-spinner></ui-spinner></div>
{/if}
</div>
</section>
{/each}
</div>`
};
}

Fez('mail-compose-dock', __fez_component_0);

export default __fez_component_0;
